import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconCheck, IconPlus } from '@tabler/icons-react';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { ConfirmDialog } from '../../components/ConfirmDialog';

// Phase HR-1: الرواتب — one run per month; draft lines are editable
// (allowance/deduction), posting freezes the run and lands the GL entry.

interface PayrollLineDto {
  payrollLineId: string;
  employeeId: string;
  employeeName: string;
  baseSalary: number;
  allowance: number;
  deduction: number;
  incomeTax: number;
  netPay: number;
}

interface PayrollRunDto {
  payrollRunId: string;
  year: number;
  month: number;
  status: number;
  totalNet: number;
  journalEntryId: string | null;
  postedAt: string | null;
  lines: PayrollLineDto[] | null;
}

const QK = ['payroll'] as const;

export function PayrollPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmPost, setConfirmPost] = useState(false);

  const runs = useQuery({
    queryKey: QK,
    queryFn: async () => (await apiClient.get<PayrollRunDto[]>('/api/hr/payroll')).data,
  });
  const detail = useQuery({
    queryKey: [...QK, selectedId] as const,
    enabled: selectedId !== null,
    queryFn: async () =>
      (await apiClient.get<PayrollRunDto>(`/api/hr/payroll/${selectedId}`)).data,
  });

  const post = useMutation({
    mutationFn: async () =>
      (await apiClient.post(`/api/hr/payroll/${selectedId}/post`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      notifications.show({ color: 'green', title: t('hr.payroll.postedToast'), message: '' });
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      }),
  });

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.hr')}
        title={t('hr.payroll.title')}
        subtitle={t('hr.payroll.subtitle')}
      />

      <Group align="flex-start" gap="lg" wrap="nowrap">
        {/* Runs list */}
        <Paper p="md" style={{ minWidth: 260 }}>
          <Stack gap="sm">
            <Button
              size="compact-md"
              leftSection={<IconPlus size={16} stroke={1.6} />}
              onClick={() => setCreateOpen(true)}
            >
              {t('hr.payroll.create')}
            </Button>
            {runs.isLoading ? (
              <Loader size="sm" />
            ) : (
              (runs.data ?? []).map((r) => (
                <Paper
                  key={r.payrollRunId}
                  p="sm"
                  withBorder
                  style={{
                    cursor: 'pointer',
                    borderColor:
                      r.payrollRunId === selectedId ? 'var(--wheels-accent)' : undefined,
                  }}
                  onClick={() => setSelectedId(r.payrollRunId)}
                >
                  <Group justify="space-between">
                    <Text size="sm" fw={600} className="font-numeric">
                      {String(r.month).padStart(2, '0')}/{r.year}
                    </Text>
                    <Badge size="xs" variant="light" radius="sm"
                      color={r.status === 2 ? 'green' : 'yellow'}>
                      {r.status === 2 ? t('hr.payroll.posted') : t('hr.payroll.draft')}
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed" className="font-numeric">
                    {r.totalNet.toFixed(2)} ₪
                  </Text>
                </Paper>
              ))
            )}
            {runs.data && runs.data.length === 0 && (
              <Text size="sm" c="dimmed">{t('hr.payroll.empty')}</Text>
            )}
          </Stack>
        </Paper>

        {/* Run detail */}
        <Paper p="md" style={{ flex: 1 }}>
          {selectedId === null ? (
            <Text size="sm" c="dimmed" ta="center" py="xl">{t('hr.payroll.pickRun')}</Text>
          ) : detail.isLoading || !detail.data ? (
            <Stack align="center" py={48}><Loader size="sm" /></Stack>
          ) : (
            <RunDetail
              run={detail.data}
              onChanged={() => {
                qc.invalidateQueries({ queryKey: QK });
              }}
              onPost={() => setConfirmPost(true)}
            />
          )}
        </Paper>
      </Group>

      <CreateRunModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          qc.invalidateQueries({ queryKey: QK });
          setSelectedId(id);
        }}
      />
      <ConfirmDialog
        opened={confirmPost}
        onClose={() => setConfirmPost(false)}
        onConfirm={() => {
          setConfirmPost(false);
          post.mutate();
        }}
        title={t('hr.payroll.postConfirmTitle')}
        message={t('hr.payroll.postConfirmMessage')}
        confirmColor="green"
        loading={post.isPending}
      />
    </Stack>
  );
}

function RunDetail({
  run, onChanged, onPost,
}: { run: PayrollRunDto; onChanged: () => void; onPost: () => void }) {
  const { t } = useTranslation();
  const isDraft = run.status === 1;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Text fw={700} className="font-numeric">
            {t('hr.payroll.runTitle', {
              month: String(run.month).padStart(2, '0'),
              year: run.year,
            })}
          </Text>
          <Badge size="sm" variant="light" radius="sm" color={isDraft ? 'yellow' : 'green'}>
            {isDraft ? t('hr.payroll.draft') : t('hr.payroll.posted')}
          </Badge>
        </Group>
        {isDraft && (
          <Button
            color="green"
            leftSection={<IconCheck size={16} stroke={1.6} />}
            onClick={onPost}
          >
            {t('hr.payroll.post')}
          </Button>
        )}
      </Group>

      <Table verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('hr.payroll.columns.employee')}</Table.Th>
            <Table.Th style={{ textAlign: 'end' }}>{t('hr.payroll.columns.base')}</Table.Th>
            <Table.Th style={{ textAlign: 'end' }}>{t('hr.payroll.columns.allowance')}</Table.Th>
            <Table.Th style={{ textAlign: 'end' }}>{t('hr.payroll.columns.deduction')}</Table.Th>
            <Table.Th style={{ textAlign: 'end' }}>{t('hr.payroll.columns.tax')}</Table.Th>
            <Table.Th style={{ textAlign: 'end' }}>{t('hr.payroll.columns.net')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(run.lines ?? []).map((l) => (
            <PayrollLineRow key={l.payrollLineId} runId={run.payrollRunId}
              line={l} editable={isDraft} onChanged={onChanged} />
          ))}
        </Table.Tbody>
        <Table.Tfoot>
          <Table.Tr>
            <Table.Td><Text fw={700}>{t('hr.payroll.total')}</Text></Table.Td>
            <Table.Td colSpan={4} />
            <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 700 }}>
              {run.totalNet.toFixed(2)}
            </Table.Td>
          </Table.Tr>
        </Table.Tfoot>
      </Table>
    </Stack>
  );
}

function PayrollLineRow({
  runId, line, editable, onChanged,
}: {
  runId: string;
  line: PayrollLineDto;
  editable: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [allowance, setAllowance] = useState(line.allowance);
  const [deduction, setDeduction] = useState(line.deduction);
  const qc = useQueryClient();

  const update = useMutation({
    mutationFn: async () =>
      (
        await apiClient.put(`/api/hr/payroll/${runId}/lines/${line.payrollLineId}`, {
          allowance,
          deduction,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll'] });
      onChanged();
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      }),
  });

  const dirty = allowance !== line.allowance || deduction !== line.deduction;

  return (
    <Table.Tr>
      <Table.Td><Text size="sm">{line.employeeName}</Text></Table.Td>
      <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
        {line.baseSalary.toFixed(2)}
      </Table.Td>
      <Table.Td style={{ textAlign: 'end', maxWidth: 120 }}>
        {editable ? (
          <NumberInput size="xs" hideControls decimalScale={2} min={0}
            value={allowance}
            onChange={(v) => setAllowance(Number(v) || 0)}
            onBlur={() => dirty && update.mutate()}
            className="font-numeric" />
        ) : (
          <span className="font-numeric">{line.allowance.toFixed(2)}</span>
        )}
      </Table.Td>
      <Table.Td style={{ textAlign: 'end', maxWidth: 120 }}>
        {editable ? (
          <NumberInput size="xs" hideControls decimalScale={2} min={0}
            value={deduction}
            onChange={(v) => setDeduction(Number(v) || 0)}
            onBlur={() => dirty && update.mutate()}
            className="font-numeric" />
        ) : (
          <span className="font-numeric">{line.deduction.toFixed(2)}</span>
        )}
      </Table.Td>
      <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
        {line.incomeTax.toFixed(2)}
      </Table.Td>
      <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 600 }}>
        {line.netPay.toFixed(2)}
      </Table.Td>
    </Table.Tr>
  );
}

function CreateRunModal({
  opened, onClose, onCreated,
}: { opened: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));

  const create = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post<PayrollRunDto>('/api/hr/payroll', {
          year: Number(year),
          month: Number(month),
        })
      ).data,
  });

  const submit = async () => {
    try {
      const run = await create.mutateAsync();
      notifications.show({ color: 'green', title: t('common.toasts.created'), message: '' });
      onCreated(run.payrollRunId);
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  const years = [0, 1, 2].map((d) => String(now.getFullYear() - d));
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1));

  return (
    <Modal opened={opened} onClose={onClose} title={t('hr.payroll.create')} centered>
      <Stack gap="md">
        <Group grow>
          <Select label={t('hr.payroll.form.monthLabel')} data={months}
            value={month} onChange={(v) => setMonth(v ?? month)} />
          <Select label={t('hr.payroll.form.yearLabel')} data={years}
            value={year} onChange={(v) => setYear(v ?? year)} />
        </Group>
        <Text size="xs" c="dimmed">{t('hr.payroll.form.hint')}</Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} loading={create.isPending}>
            {t('common.create')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
